// What the port screens show for a port (DESIGN.md "Bring your numbers", mockups e to h3): the
// numbers a pasted list holds, the badge of each state, the timeline, who may edit or cancel,
// what still blocks signing, and the letter of authorization read back from its PDF.
import type { ExcludedNumber, Port, PortEdit, PortState } from "../api/ports";
import type { Tone } from "../components/ui";

/** The entries of a pasted list: one per line, or separated by commas or semicolons. */
export function parseNumbers(text: string): string[] {
  return text
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export const MAX_CHECK = 100;

/** "1 number", "7 numbers". */
export function numbersCount(n: number): string {
  return `${n} ${n === 1 ? "number" : "numbers"}`;
}

/** The badge of each state (DESIGN.md "Port page"). */
export const STATE_BADGE: Record<PortState, { label: string; tone: Tone }> = {
  draft: { label: "Draft", tone: "neutral" },
  signed: { label: "Signed", tone: "neutral" },
  submitting: { label: "Submitted", tone: "neutral" },
  submitted: { label: "Submitted", tone: "neutral" },
  needs_info: { label: "Needs a fix", tone: "warn" },
  foc_confirmed: { label: "Date confirmed", tone: "neutral" },
  porting: { label: "Switching today", tone: "accent" },
  ported: { label: "Ported", tone: "ok" },
  cancelled: { label: "Canceled", tone: "neutral" },
  needs_attention: { label: "We're checking", tone: "neutral" },
};

/** Draft, signed and needs a fix: the Admin edits the details (a signed port signs again). */
export function isEditable(state: PortState): boolean {
  return state === "draft" || state === "signed" || state === "needs_info";
}

/** Before the switch, the Admin may cancel. */
export function canCancel(state: PortState): boolean {
  return state === "draft" || state === "signed" || state === "submitted" || state === "foc_confirmed";
}

/** The page shows the form (draft and signed) or the status (everything after). */
export function showsForm(state: PortState): boolean {
  return state === "draft" || state === "signed";
}

/** "AT&T port". */
export function portName(p: Pick<Port, "losing_carrier">): string {
  return `${p.losing_carrier} port`;
}

/** Why a checked number can't move, in the customer's words. */
export function excludedReason(x: ExcludedNumber): string {
  if (x.detail?.trim()) return x.detail.trim();
  switch (x.reason) {
    case "no_carrier":
      return "No current carrier found";
    case "duplicate":
      return "Listed twice";
    default:
      return "Can't move yet";
  }
}

export type StepMark = "done" | "current" | "problem" | "ahead";

export interface TimelineStep {
  key: "submitted" | "fix" | "confirmed" | "switching" | "ported";
  title: string;
  mark: StepMark;
  /** When, as an ISO time, if known. */
  at?: string | null;
}

/** The status card's steps: Submitted, Date confirmed, Switching, Ported; a fix replaces the
 * steps after Submitted with the carrier's request. */
export function timeline(p: Port): TimelineStep[] {
  const s = p.state;
  if (s === "needs_info") {
    return [
      { key: "submitted", title: "Submitted", mark: "done", at: p.submitted_at },
      {
        key: "fix",
        title: `${p.losing_carrier} needs a fix`,
        mark: "problem",
        at: p.carrier_comments[0]?.at ?? null,
      },
      { key: "confirmed", title: "Date confirmed", mark: "ahead" },
      { key: "ported", title: "Ported", mark: "ahead" },
    ];
  }
  const order: PortState[] = ["submitted", "foc_confirmed", "porting", "ported"];
  const at = s === "submitting" ? 0 : order.indexOf(s);
  const markOf = (i: number): StepMark => {
    if (s === "ported") return "done";
    if (at < 0) return i === 0 && p.submitted_at ? "done" : "ahead";
    if (i < at) return "done";
    if (i === at) return i === 0 ? "done" : "current";
    return "ahead";
  };
  return [
    { key: "submitted", title: "Submitted", mark: markOf(0), at: p.submitted_at },
    { key: "confirmed", title: "Date confirmed", mark: markOf(1), at: p.foc_at },
    { key: "switching", title: "Switching", mark: markOf(2) },
    { key: "ported", title: "Ported", mark: markOf(3) },
  ];
}

/** Each number's state on the port page. */
export function numberState(state: PortState): { label: string; tone: Tone; busy?: boolean } {
  if (state === "ported") return { label: "On Kite", tone: "ok" };
  if (state === "porting") return { label: "Switching", tone: "neutral", busy: true };
  return { label: "Waiting", tone: "neutral" };
}

/** The form's values, as typed. */
export interface PortForm {
  account_holder: string;
  authorized_name: string;
  authorized_title: string;
  billing_number: string;
  /** Typed only; empty keeps the one on file. */
  account_number: string;
  /** Typed only; empty keeps the one on file. */
  pin: string;
  street: string;
  unit: string;
  zip: string;
  city: string;
  state: string;
  kind: "full" | "partial";
  partial_remaining: string;
  requested_foc_date: string;
  numbers: {
    e164: string;
    number_type: "local" | "toll_free";
    brand_id: string;
    owner_user_id: string;
    label: string;
    emergency_address_id: string;
  }[];
}

export function formOf(p: Port): PortForm {
  return {
    account_holder: p.account_holder,
    authorized_name: p.authorized_name,
    authorized_title: p.authorized_title,
    billing_number: p.billing_number ?? "",
    account_number: "",
    pin: "",
    street: p.service_address.street,
    unit: p.service_address.unit ?? "",
    zip: p.service_address.zip ?? "",
    city: p.service_address.city,
    state: (p.service_address.state ?? "").toUpperCase(),
    kind: p.kind,
    partial_remaining: p.partial_remaining ?? "",
    requested_foc_date: p.requested_foc_date ?? "",
    numbers: p.numbers.map((n) => ({
      e164: n.e164,
      number_type: n.number_type,
      brand_id: n.brand_id ?? "",
      owner_user_id: n.owner_user_id ?? "",
      label: n.label,
      emergency_address_id: n.emergency_address_id ?? "",
    })),
  };
}

/** The `PUT` body: every field, the account number and PIN only when typed. */
export function editOf(f: PortForm): PortEdit {
  const t = (s: string) => s.trim();
  return {
    kind: f.kind,
    partial_remaining: f.kind === "partial" ? t(f.partial_remaining) || null : null,
    account_holder: t(f.account_holder),
    authorized_name: t(f.authorized_name),
    authorized_title: t(f.authorized_title),
    billing_number: t(f.billing_number) || null,
    service_address: {
      street: t(f.street),
      unit: t(f.unit) || null,
      city: t(f.city),
      state: f.state,
      zip: t(f.zip),
    },
    ...(t(f.account_number) ? { account_number: t(f.account_number) } : {}),
    ...(t(f.pin) ? { pin: t(f.pin) } : {}),
    requested_foc_date: f.requested_foc_date || null,
    numbers: f.numbers.map((n) => ({
      e164: n.e164,
      brand_id: n.brand_id || null,
      owner_user_id: n.owner_user_id || null,
      label: t(n.label) || null,
      emergency_address_id: n.emergency_address_id || null,
    })),
  };
}

/** What still blocks Review and sign; empty when the port can be signed. */
export function signBlockers(
  f: PortForm,
  p: Pick<Port, "account_number_last4" | "bill_uploaded" | "earliest_foc_date">,
): string[] {
  const out: string[] = [];
  const empty = (s: string) => s.trim() === "";
  if (empty(f.account_holder)) out.push("account_holder");
  if (empty(f.authorized_name)) out.push("authorized_name");
  if (empty(f.authorized_title)) out.push("authorized_title");
  if (empty(f.billing_number)) out.push("billing_number");
  if (empty(f.account_number) && !p.account_number_last4) out.push("account_number");
  if (empty(f.street)) out.push("street");
  if (empty(f.city)) out.push("city");
  if (empty(f.state)) out.push("state");
  if (empty(f.zip)) out.push("zip");
  if (f.kind === "partial" && empty(f.partial_remaining)) out.push("partial_remaining");
  if (!f.requested_foc_date || f.requested_foc_date < p.earliest_foc_date) out.push("requested_foc_date");
  if (!p.bill_uploaded) out.push("bill");
  if (f.numbers.some((n) => n.number_type === "local" && !n.emergency_address_id))
    out.push("emergency_address");
  return out;
}

/** "Nov 12": the earliest date, for the Date help. */
export function shortDate(iso: string): string {
  const d = new Date(`${iso}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** "Fri, Nov 14, 9:00 AM CST", in the browser's time zone. */
export function focText(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      });
}

/** "Oct 28, 10:12 AM". */
export function stepTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/** One line of the letter as the PDF sets it. */
export interface LoaLine {
  text: string;
  bold: boolean;
  size: number;
}

/**
 * The letter's lines read back from its PDF (crates/server/src/ports/loa.rs writes uncompressed
 * `BT /F1 11 Tf x y Td (text) Tj ET` runs in WinAnsi), so the Admin reads the very document the
 * signature's SHA-256 covers.
 */
export function loaLines(pdf: ArrayBuffer): LoaLine[] {
  const raw = new TextDecoder("latin1").decode(pdf);
  const out: LoaLine[] = [];
  const re = /BT \/(F[12]) (\d+) Tf \d+ \d+ Td \(((?:\\.|[^\\)])*)\) Tj ET/g;
  for (const m of raw.matchAll(re)) {
    out.push({
      bold: m[1] === "F2",
      size: Number(m[2]),
      text: (m[3] ?? "").replace(/\\(.)/g, "$1"),
    });
  }
  return out;
}
